import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Apollo } from 'apollo-angular';
import { KeycloakService } from 'keycloak-angular';
import { Observable, of, switchMap } from 'rxjs';
import { Tenant, TenantInput } from '../../entities/tenant/tenant';
import { ApolloService } from '../apollo.service';
import { UsersService } from '../users/users.service';
import * as fromGraphQl from './graphql';

/**
 * The users service.
 */
@Injectable()
export class TenantsService {
  public theme = 'standard';
  private renderer: Renderer2;
  constructor(
    private apolloService: ApolloService,
    private apollo: Apollo,
    private keycloakAngular: KeycloakService,
    rendererFactory: RendererFactory2,
    private userService: UsersService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }


  public getCurrentTenant(
    fragment: string = fromGraphQl.DEFAULT_TENANT_FRAGMENT,
    fragmentName: string = fromGraphQl.DEFAULT_TENANT_FRAGMENT_NAME
  ): Observable<Tenant> {
    let currentTenantId = this.userService.getCurrentTenantId();
    return this.apolloService
      .query<fromGraphQl.TenantQueryResponse>(fromGraphQl.getTenantQuery(
        fragment, fragmentName
      ),
        {
          id: currentTenantId
        })
      .pipe(
        switchMap((result) => {
          return of(new Tenant(result.data.tenant));
        })
      );
  }

   /**
   * creates a tenant
   * @param tenantInput user Input
   * @param callback method
   */
    public createTenant(tenantInput: TenantInput, callback: any): void {
      this.apolloService.mutateAndExecute(
        fromGraphQl.CREATE_TENANT_MUTATION,
        {
          input: tenantInput,
        },
        callback
      );
    }

     /**
   * updates a tenant
   * @param tenantInput user Input
   * @param callback method
   */
  public updateTenant(tenantInput: TenantInput, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_TENANT_MUTATION,
      {
        input: tenantInput,
      },
      callback
    );
  }

  /**
   * updates a tenant
   * @param tenantInput user Input
   * @param callback method
   */
  public updateInvoiceMail(id: string, invoiceMail: string, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_INVOICEMAIL_MUTATION,
      {
        id,
        invoiceMail
      },
      callback
    );
  }

  /**
   * updates the invoice address of a tenant
   * @param tenantInput user Input
   * @param callback method
   */
  public updateInvoiceAddress(input: any, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_INVOICE_ADDRESS_MUTATION,
      {
        input
      },
      callback
    );
  }

  public deleteInvoiceAddress(id: string, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.DELETE_INVOICE_ADDRESS_MUTATION,
      {
        id
      },
      callback
    );
  }

  public deleteLicenceRequests(tenantId: string, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.DELETE_LICENCE_REQUESTS_MUTATION,
      {
        tenantId
      },
      callback
    );
  }

  public deleteInvoiceEmail(id: string, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.DELETE_INVOICE_EMAIL_MUTATION,
      {
        id
      },
      callback
    );
  }

  /**
   * updates an user
   * @param tenant
   * returns boolean
   */
   public switchTenant(tenant: string): void {
    this.apollo
      .mutate({
        mutation: fromGraphQl.SWITCH_TENANT_MUTATION,
        variables: {
          tenant,
        },
      })
      .subscribe(
        ({ }) => {
          this.keycloakAngular.updateToken(-1).then(function(success): void {
            window.location.reload();
          });
        },
        (error) => {
          console.log('there was an error sending the query', error);
        }
      );
  }

  getVatTypes () {
    let types = [
      { id: 'CH_UID', name: 'CH UID' },
      { id: 'CH_VAT', name: 'CH VAT' },
      { id: 'DE_STN', name: 'DE STN' },
      { id: 'EU_VAT', name: 'EU VAT' }
    ];
    return types;
  }

  getVatTypeName(value: any): string {
    return this.getVatTypes().find((vatType) => vatType.id === value)?.name;
  }

  vatNumberValidator(): ValidatorFn {
    // VAT / tax number formats for all EU member states + Switzerland
    const patterns = [
      'ATU\\d{8}',                                  // Austria (AT)
      'BE[01]\\d{9}',                               // Belgium (BE)
      'BG\\d{9,10}',                                // Bulgaria (BG)
      'HR\\d{11}',                                  // Croatia (HR)
      'CY\\d{8}[A-Z]',                              // Cyprus (CY)
      'CZ\\d{8,10}',                                // Czech Republic (CZ)
      'DK\\d{8}',                                   // Denmark (DK)
      'EE\\d{9}',                                   // Estonia (EE)
      'FI\\d{8}',                                   // Finland (FI)
      'FR[A-Z0-9]{2}\\d{9}',                        // France (FR)
      'DE\\d{9}',                                   // Germany VAT ID (DE)
      '\\d{11}',                                    // Germany Steuernummer (DE_STN)
      'EL\\d{9}',                                   // Greece (EL)
      'HU\\d{8}',                                   // Hungary (HU)
      'IE(\\d{7}[A-Z]{1,2}|\\d[A-Z+*]\\d{5}[A-Z])', // Ireland (IE)
      'IT\\d{11}',                                  // Italy (IT)
      'LV\\d{11}',                                  // Latvia (LV)
      'LT(\\d{9}|\\d{12})',                         // Lithuania (LT)
      'LU\\d{8}',                                   // Luxembourg (LU)
      'MT\\d{8}',                                   // Malta (MT)
      'NL\\d{9}B\\d{2}',                            // Netherlands (NL)
      'PL\\d{10}',                                  // Poland (PL)
      'PT\\d{9}',                                   // Portugal (PT)
      'RO\\d{2,10}',                                // Romania (RO)
      'SK\\d{10}',                                  // Slovakia (SK)
      'SI\\d{8}',                                   // Slovenia (SI)
      'ES[A-Z0-9]\\d{7}[A-Z0-9]',                  // Spain (ES)
      'SE\\d{12}',                                  // Sweden (SE)
      'CHE\\d{9}(MWST)?',                           // Switzerland (CH)
    ];
    const vatAndTaxRegex = new RegExp(`^(${patterns.join('|')})$`);

    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = vatAndTaxRegex.test(control.value);
      return isValid ? null : { invalidVatOrTax: true };
    };
  }
}
